/**
 * Display names for the knowledge sources in the Copilot (beta) "Sources" picker.
 *
 * Deliberately free of React so the rules can be unit-tested directly — the same reasoning as
 * utils/nav.ts.
 *
 * ── Why the renaming happens HERE and not in AVSOS ─────────────────────────────────────────────
 *
 * The keys are a contract: the picker sends them straight back as `sourceTypes` on the next chat
 * request (CopilotTwo -> avsOsSearch -> AVS OS). Renaming upstream would either change those keys —
 * breaking every stored selection and any other consumer of the same capabilities endpoint — or
 * leave key and label disagreeing on the server. This is a presentation concern, so it lives in the
 * presentation layer and only ever touches `label`.
 *
 * ── Why it matches on key OR label ─────────────────────────────────────────────────────────────
 *
 * The capability list is served by AVS OS and could not be read while writing this: the endpoint
 * requires an `ai_search` permission the available key does not carry. The KB area keys ARE known
 * (kb_areas, 2026-08-28: avsight, internal_knowledge, intelligence, copilot), and the two labels
 * being renamed were given verbatim. So each rule fires on either signal, and anything matching
 * neither is passed through UNCHANGED — a source this module has never heard of keeps the name AVS
 * OS gave it rather than being silently mislabelled or dropped.
 */

/** Prefix marking a source that is knowledge-base content. */
export const KB_PREFIX = "KB: ";

/**
 * KB areas that get the prefix plus a tidied name.
 *
 * `internal_knowledge` becomes "KB: Internal" rather than "KB: Internal Knowledge" — with the
 * prefix already saying it is knowledge, the word twice reads as a stutter (David, 2026-08-28).
 */
const KB_AREAS: Record<string, string> = {
  avsight: "AvSight",
  intelligence: "Intelligence",
  copilot: "Copilot",
  internal_knowledge: "Internal",
};

/** Labels that identify a KB area when the key is not one this module recognises. */
const KB_AREA_BY_LABEL: Record<string, string> = {
  "avsight": "AvSight",
  "intelligence": "Intelligence",
  "copilot": "Copilot",
  "internal knowledge": "Internal",
};

/** Sources renamed outright rather than prefixed, by key and by label. */
const RENAMES: Record<string, string> = { product_knowledge: "Source Code" };
const RENAMES_BY_LABEL: Record<string, string> = { "product knowledge": "Source Code" };

function normalise(value: unknown): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

/**
 * What to show the reader for one capability.
 *
 * Idempotent: a label that already carries the prefix is returned untouched, so re-running this
 * over an already-mapped list cannot produce "KB: KB: AvSight".
 *
 * @param capability the capability as AVS OS served it — `key` and `label` are both consulted
 */
export function displaySourceLabel(capability: { key?: unknown; label?: unknown }): string {
  const label = typeof capability?.label === "string" ? capability.label : "";
  const key = normalise(capability?.key);
  const labelKey = normalise(label);

  // Already mapped. Never prefix twice.
  if (label.startsWith(KB_PREFIX)) return label;

  const renamed = RENAMES[key] ?? RENAMES_BY_LABEL[labelKey];
  if (renamed) return renamed;

  const area = KB_AREAS[key] ?? KB_AREA_BY_LABEL[labelKey];
  if (area) return KB_PREFIX + area;

  // Not a source this module knows about. AVS OS's own label stands.
  return label;
}
